/**
 * Utility functions for Clerk CAPTCHA bot protection
 */

/**
 * Check if an error is CAPTCHA-related
 * @param error - The error object from Clerk
 * @returns boolean indicating if the error is CAPTCHA-related
 */
export const isCaptchaError = (error: any): boolean => {
  // Check for specific CAPTCHA error codes
  if (error.errors && error.errors.length > 0) {
    return error.errors.some((err: any) => 
      err.code === "captcha_invalid" || 
      err.code === "captcha_required" || 
      err.code === "captcha_failed"
    );
  }
  
  // Check error message for CAPTCHA-related keywords
  const errorMessage = error.message || '';
  return errorMessage.toLowerCase().includes('captcha') || 
         errorMessage.toLowerCase().includes('bot');
};

/**
 * Get a user-friendly CAPTCHA error message
 * @param error - The error object from Clerk
 * @returns string with appropriate error message
 */
export const getCaptchaErrorMessage = (error: any): string => {
  if (error.errors && error.errors.length > 0) {
    const captchaError = error.errors.find((err: any) => 
      err.code === "captcha_invalid" || 
      err.code === "captcha_required" || 
      err.code === "captcha_failed"
    );
    
    if (captchaError) {
      switch (captchaError.code) {
        case "captcha_required":
          return "Please complete the CAPTCHA verification to continue.";
        case "captcha_invalid":
          return "CAPTCHA verification failed. Please try again.";
        case "captcha_failed":
          return "CAPTCHA verification failed. Please refresh the page and try again.";
        default:
          return "Please complete the CAPTCHA verification to continue.";
      }
    }
  }
  
  return "CAPTCHA verification is required. Please complete the CAPTCHA and try again.";
};

/**
 * Check if CAPTCHA element exists in the DOM
 * @returns boolean indicating if the CAPTCHA element is present
 */
export const isCaptchaElementPresent = (): boolean => {
  return document.getElementById('clerk-captcha') !== null;
};

/**
 * Ensure CAPTCHA element exists and is properly configured
 * @param theme - CAPTCHA theme ('light', 'dark', 'auto')
 * @param size - CAPTCHA size ('normal', 'flexible', 'compact')
 * @param language - CAPTCHA language ('auto' or language code)
 * @returns boolean indicating if CAPTCHA element was created or already exists
 */
export const ensureCaptchaElement = (
  theme: 'light' | 'dark' | 'auto' = 'auto',
  size: 'normal' | 'flexible' | 'compact' = 'normal',
  language: string = 'auto'
): boolean => {
  let captchaElement = document.getElementById('clerk-captcha');
  
  if (!captchaElement) {
    // Create CAPTCHA element if it doesn't exist
    captchaElement = document.createElement('div');
    captchaElement.id = 'clerk-captcha';
    captchaElement.setAttribute('data-cl-theme', theme);
    captchaElement.setAttribute('data-cl-size', size);
    captchaElement.setAttribute('data-cl-language', language);
    captchaElement.style.display = 'flex';
    captchaElement.style.alignItems = 'center';
    captchaElement.style.justifyContent = 'center';
    captchaElement.style.marginBottom = '16px';
    
    // Try to append to a form or body
    const form = document.querySelector('form');
    if (form) {
      form.appendChild(captchaElement);
    } else {
      document.body.appendChild(captchaElement);
    }
    
    console.warn('CAPTCHA element was missing and has been created dynamically. Consider adding it to your JSX for better UX.');
    return true;
  }
  
  return true;
};

/**
 * Reset CAPTCHA widget (useful for retry scenarios)
 */
export const resetCaptcha = (): void => {
  const captchaElement = document.getElementById('clerk-captcha');
  if (captchaElement) {
    // Clear the element content to force re-render
    captchaElement.innerHTML = '';
  }
};

/**
 * CAPTCHA configuration interface
 */
export interface CaptchaConfig {
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'flexible' | 'compact';
  language?: string;
}

/**
 * Default CAPTCHA configuration
 */
export const defaultCaptchaConfig: CaptchaConfig = {
  theme: 'auto',
  size: 'normal',
  language: 'auto'
};

/**
 * Validate CAPTCHA configuration
 * @param config - CAPTCHA configuration object
 * @returns boolean indicating if configuration is valid
 */
export const validateCaptchaConfig = (config: CaptchaConfig): boolean => {
  const validThemes = ['light', 'dark', 'auto'];
  const validSizes = ['normal', 'flexible', 'compact'];
  
  if (config.theme && !validThemes.includes(config.theme)) {
    console.error(`Invalid CAPTCHA theme: ${config.theme}. Valid options: ${validThemes.join(', ')}`);
    return false;
  }
  
  if (config.size && !validSizes.includes(config.size)) {
    console.error(`Invalid CAPTCHA size: ${config.size}. Valid options: ${validSizes.join(', ')}`);
    return false;
  }
  
  return true;
}; 